import React, { useState } from 'react'

import { Button, Input, Label, toast } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { authApi } from '@/api/auth.api'
import authBg from '@/assets/auth-bg.png'
import { Card, CardContent } from '@/components/ui/Card'

export default function ResetPasswordPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!token) {
      toast.danger(t('auth.reset.invalid_token'))
      return
    }

    if (newPassword !== confirmPassword) {
      toast.danger(t('auth.reset.error_mismatch'))
      return
    }

    setIsSubmitting(true)
    try {
      await authApi.resetPassword({ token, newPassword })
      toast.success(
        t('auth.reset.success_toast')
      )
      navigate('/login')
    } catch (err) {
      toast.danger((err as Error).message || t('common.error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-center p-5 relative"
      style={{ backgroundImage: `url(${authBg})` }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-0 transition" />

      <div className="w-full max-w-md relative z-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
        <div className="text-center mb-10">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-blue-600 items-center justify-center text-white text-2xl font-bold mb-4 shadow-xl shadow-blue-500/20">
            W
          </div>
          <h1 className="text-3xl font-black text-white tracking-tight mb-2">
            {t('auth.company_name')}
          </h1>
          <p className="text-white/50 font-medium">
            {t('auth.system_name')}
          </p>
        </div>

        <Card className="bg-white/5 border-white/10 shadow-2xl">
          <CardContent className="p-8">
            <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
              <div className="text-center mb-2">
                <h2 className="text-xl font-bold text-white mb-2">
                  {t('auth.reset.title')}
                </h2>
              </div>

              {!token ? (
                <div className="text-red-400 text-sm text-center py-4">
                  {t('auth.reset.invalid_token')}
                </div>
              ) : (
                <>
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-xs font-bold text-white/40 uppercase tracking-wider ml-1">
                      {t('auth.reset.new_password')}
                    </Label>
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      variant={'secondary'}
                      required
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label className="text-xs font-bold text-white/40 uppercase tracking-wider ml-1">
                      {t('auth.reset.confirm_password')}
                    </Label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      variant={'secondary'}
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    fullWidth
                    isDisabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('auth.reset.submitting')}
                      </div>
                    ) : t('auth.reset.submit')}
                  </Button>
                </>
              )}

              <Button
                type="button"
                variant="ghost"
                fullWidth
                onClick={() => navigate('/login')}
                className="text-white/75 hover:text-white"
              >
                {t('auth.back_to_login')}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="mt-10 text-center">
          <p className="text-white/40 text-xs font-bold uppercase tracking-widest">
            &copy; 2026 {t('auth.company_name')}
          </p>
        </div>
      </div>
    </div>
  )
}
